import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import complementationImg from '../images/complementation.png';
import activateImg from '../images/activate.png';
import InstrumentalitySimulator from './InstrumentalitySimulator';

const MainLanding = () => {
  const [stage, setStage] = useState('main'); // main -> warning (mobile only) -> transitioning -> game
  const [fadeOut, setFadeOut] = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileWarningShown, setMobileWarningShown] = useState(false);

  // Check if the user is on a mobile device
  useEffect(() => {
    const checkMobile = () => {
      const mobileCheck = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
      setIsMobile(mobileCheck);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle clicks on the activate button
  const handleActivateClick = () => {
    // For mobile, show warning first
    if (isMobile && !mobileWarningShown) {
      // Simply switch to warning screen without fading
      setMobileWarningShown(true);
    } else {
      // For desktop or mobile that's already seen warning, proceed to game
      enterGame();
    }
  };

  // Handle the transition to the game
  const enterGame = () => {
    // Start fade out animation
    setFadeOut(true);
    
    // Set transitioning state after fade out
    setTimeout(() => {
      setStage('transitioning');
      
      // Start showing the simulator
      setTimeout(() => {
        setShowSimulator(true);
        
        // Complete the transition after simulator is visible
        setTimeout(() => {
          setStage('game');
        }, 1200);
      }, 500);
    }, 1000);
  };

  // Render transitioning state with simple fade in
  if (stage === 'transitioning') {
    return (
      <div style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        width: '100vw', 
        height: '100vh',
        background: 'black',
        overflow: 'hidden'
      }}>
        {showSimulator && (
          <div style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            animation: 'fadeIn 1.5s ease-in-out forwards'
          }}>
            <InstrumentalitySimulator />
          </div>
        )}
        <style jsx>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  // Render game when fully transitioned
  if (stage === 'game') {
    return <InstrumentalitySimulator />;
  }

  // Mobile warning view - shown after clicking activate on mobile
  if (isMobile && mobileWarningShown) {
    return (
      <div className="warning-container"
        style={{
          backgroundColor: '#000',
          color: '#fff',
          height: '100vh',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          fontFamily: '"Courier New", monospace',
          padding: '20px',
          textAlign: 'center'
        }}>
        <div style={{ 
          transform: 'scaleY(1.5)',
          transformOrigin: 'top',
          letterSpacing: '2px',
          fontWeight: 'bold',
          // color: '#bd93f9',
          marginBottom: '6rem',
          fontSize: '1.5rem',
          fontFamily: 'Matisse'
        }}>
          HUMAN INSTRUMENTALITY PROJECT
        </div>
        
        <div style={{
          padding: '20px',
          border: '1px solid rgba(255, 85, 85, 0.3)',
          borderRadius: '5px',
          boxShadow: '0 0 10px rgba(255, 85, 85, 0.2)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          maxWidth: '80%'
        }}>
          <p style={{ color: '#ff5555', marginBottom: '1rem' }}>
            TERMINAL ACCESS RESTRICTED
          </p>
          <p style={{ color: '#ffffff', marginBottom: '1rem', lineHeight: '1.6' }}>
            This experience is designed for desktop terminals only.
          </p>
          <p style={{ color: '#aaaaaa', fontSize: '0.9rem' }}>
            Please access from a desktop device for optimal experience.
          </p>
        </div>
        
        <div style={{ 
          marginTop: '2rem', 
          cursor: 'pointer',
          color: '#6ea284',
          textDecoration: 'underline'
        }} onClick={enterGame}>
          OVERRIDE RESTRICTION
        </div>
      </div>
    );
  }

  // Main landing page (same for desktop and mobile initially)
  return (
    <div 
      className={`landing-container ${fadeOut ? 'fade-out' : ''}`}
      style={{
        backgroundColor: '#000',
        color: '#fff',
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: '"Courier New", monospace',
        padding: 0,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Effects */}
      <div className="bg-effect" style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'radial-gradient(circle at center, rgba(255, 85, 85, 0.05) 0%, rgba(0, 0, 0, 0) 70%)',
        zIndex: 0
      }}></div>
      
      {/* Vertical Lines */}
      <div className="vertical-line" style={{
        position: 'absolute',
        top: '5vh',
        left: '20vw',
        width: '1px',
        height: '90vh',
        backgroundColor: 'rgba(255, 85, 85, 0.2)',
        boxShadow: '0 0 8px rgba(255, 85, 85, 0.2)'
      }}></div>
      
      <div className="vertical-line" style={{
        position: 'absolute',
        top: '5vh',
        right: '20vw',
        width: '1px',
        height: '90vh',
        backgroundColor: 'rgba(255, 85, 85, 0.2)',
        boxShadow: '0 0 8px rgba(255, 85, 85, 0.2)'
      }}></div>

      {/* Logo Container */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 2, ease: "easeOut" }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2,
          marginBottom: '4rem',
          width: '100%',
          padding: '0 20px'
        }}
      >
        {/* Logo */}
        <div style={{ 
          width: '100vw', 
          maxWidth: '1000px',
          marginBottom: '1rem'
        }}>
          <img 
            src={complementationImg} 
            alt="Human Instrumentality Project"
            style={{ 
              width: '100%', 
              height: 'auto',
              filter: 'drop-shadow(0 0 15px rgba(255, 85, 85, 0.3))'
            }} 
          />
        </div>
        
        {/* Activation Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 2 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleActivateClick}
          style={{
            cursor: 'pointer',
            width: '200px',
            marginTop: '2rem',
            position: 'relative'
          }}
        >
          <img 
            src={activateImg} 
            alt="Activate" 
            style={{ 
              width: '100%', 
              height: 'auto',
              filter: 'drop-shadow(0 0 10px rgba(255, 85, 85, 0.5))'
            }} 
          />
          
          {/* Pulse ring removed */}
        </motion.div>
      </motion.div>

      {/* Footer */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 3 }}
        style={{ 
          position: 'absolute',
          bottom: '2rem',
          fontSize: '0.8rem',
          color: 'rgba(255, 255, 255, 0.3)',
          letterSpacing: '2px'
        }}
      >
        SRI SARAF
      </motion.p>

      {/* CSS Animations */}
      <style jsx>{`
        .fade-out {
          animation: fadeOut 1.2s ease-in-out forwards;
        }
        
        @keyframes fadeOut {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        
        .vertical-line {
          animation: verticalFade 3s infinite alternate;
        }
        
        @keyframes verticalFade {
          0% { opacity: 0.2; }
          100% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default MainLanding; 